/**
 * Turns Titan's baked class-map imagery into {@link reliefHeightM} samples at
 * runtime: no DOM, no three.js, so a Node check reads the same field the
 * browser draws. The browser-only webp decode lives in `tileReliefDecode.ts`.
 */
import {
  reliefHeightM,
  type ClassWeights, type ReliefConfig, type ReliefSample,
} from './proceduralRelief.ts';
import { faceTangent, type TileBounds, type Vec3 } from './tileTree.ts';

/** A decoded equirectangular class map: row 0 is north (+90 lat), column 0 is
 *  lon -180, east positive. RGBA, 4 bytes per pixel. */
export interface DecodedClassMap {
  readonly data: Uint8Array | Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
}

/** Everything a sample reads besides its direction: plain data, so it crosses to a worker. */
export interface TileReliefMaps {
  readonly terrainMap: DecodedClassMap;
  readonly duneMap: DecodedClassMap;
  readonly config: ReliefConfig;
}

/** The sampler's debug switches, as one evaluation reads them. */
export interface TileReliefSwitches {
  readonly reliefInert: boolean;
  readonly reliefUnconditioned: boolean;
  readonly reliefRepeat: number;
}

export interface TileReliefSampler extends TileReliefSwitches {
  /** `direction` is a unit body-fixed vector, z the pole. */
  heightM(x: number, y: number, z: number, cellM?: number): number;
  readonly maps: TileReliefMaps;
  /** Debug switch: every sample is 0, the uniform-at-0 the caller already draws. */
  reliefInert: boolean;
  /** Debug switch: every point is pure dune (weight 1), orientation still
   *  read from the dune map - isolates the dune generator from the class blend. */
  reliefUnconditioned: boolean;
  /** Debug switch: how many times each sample is evaluated, the same answer
   *  every time; above 1 it only makes a tile build slower. */
  reliefRepeat: number;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function wrapCol(c: number, width: number): number {
  const m = c % width;
  return m < 0 ? m + width : m;
}

function clampRow(r: number, height: number): number {
  return Math.min(height - 1, Math.max(0, r));
}

const poleMeans = new WeakMap<DecodedClassMap, { north: number[]; south: number[] }>();

/** Every channel's mean over the first and last rows: the one value each pole can have. */
function poleMeansFor(map: DecodedClassMap): { north: number[]; south: number[] } {
  const hit = poleMeans.get(map);
  if (hit) return hit;
  const { data, width, height } = map;
  const north = [0, 0, 0, 0];
  const south = [0, 0, 0, 0];
  for (let x = 0; x < width; x++) {
    for (let ch = 0; ch < 4; ch++) {
      north[ch]! += (data[x * 4 + ch] ?? 0) / width;
      south[ch]! += (data[((height - 1) * width + x) * 4 + ch] ?? 0) / width;
    }
  }
  const means = { north, south };
  poleMeans.set(map, means);
  return means;
}

/** Bilinear sample of every channel at once, wrapping the lon seam. Past the
 *  outermost row centres it eases onto that row's mean, so a pole reads one
 *  value from every longitude instead of whichever column the lon picks.
 *  `lonDeg`/`latDeg` are this repo's shipped-globe convention (east positive, -180..180). */
function bilinearChannels(map: DecodedClassMap, lonDeg: number, latDeg: number): [number, number, number, number] {
  const { data, width, height } = map;
  const u = ((lonDeg + 180) / 360) * width;
  const v = ((90 - latDeg) / 180) * height;
  const fx = u - 0.5;
  const fy = v - 0.5;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const cx0 = wrapCol(x0, width);
  const cx1 = wrapCol(x0 + 1, width);
  const cy0 = clampRow(y0, height);
  const cy1 = clampRow(y0 + 1, height);
  const out: [number, number, number, number] = [0, 0, 0, 0];
  for (let ch = 0; ch < 4; ch++) {
    const c00 = data[(cy0 * width + cx0) * 4 + ch] ?? 0;
    const c10 = data[(cy0 * width + cx1) * 4 + ch] ?? 0;
    const c01 = data[(cy1 * width + cx0) * 4 + ch] ?? 0;
    const c11 = data[(cy1 * width + cx1) * 4 + ch] ?? 0;
    const top = c00 + (c10 - c00) * tx;
    const bot = c01 + (c11 - c01) * tx;
    out[ch] = top + (bot - top) * ty;
  }
  const cap = fy < 0 ? 'north' : fy > height - 1 ? 'south' : null;
  if (cap !== null) {
    const toPole = cap === 'north' ? Math.min(1, -2 * fy) : Math.min(1, 2 * (fy - (height - 1)));
    const mean = poleMeansFor(map)[cap];
    for (let ch = 0; ch < 4; ch++) out[ch] = out[ch]! + ((mean[ch] ?? 0) - out[ch]!) * toPole;
  }
  return out;
}

function normalizeBearingDeg(deg: number): number {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

/** Terrain map: R dune, G lake, B rugged, A plains, each 0-255. Dune map: R/G
 *  the double-angle vector, bilinear-interpolated as a vector before the
 *  single `atan2` below, so there is no branch-cut wall at the wrap. The dune
 *  map's own alpha duplicates the terrain map's dune weight and is not read. */
export function sampleClasses(
  terrainMap: DecodedClassMap, duneMap: DecodedClassMap, lonDeg: number, latDeg: number,
): { classes: ClassWeights; duneOrientationDeg: number } {
  const terrain = bilinearChannels(terrainMap, lonDeg, latDeg);
  const dune = bilinearChannels(duneMap, lonDeg, latDeg);
  const classes: ClassWeights = {
    dune: clamp01(terrain[0] / 255),
    lake: clamp01(terrain[1] / 255),
    rugged: clamp01(terrain[2] / 255),
    plains: clamp01(terrain[3] / 255),
  };
  const theta = 0.5 * Math.atan2(dune[1] - 128, dune[0] - 128);
  const bearingDeg = normalizeBearingDeg(90 - theta * (180 / Math.PI));
  return { classes, duneOrientationDeg: bearingDeg };
}

/** One sample of the field: the sampler's `heightM` and a worker's lattice both come through here. */
export function reliefHeightAt(
  maps: TileReliefMaps, switches: TileReliefSwitches, x: number, y: number, z: number, cellM?: number,
): number {
  if (switches.reliefInert) return 0;
  const cz = Math.min(1, Math.max(-1, z));
  const latDeg = Math.asin(cz) * (180 / Math.PI);
  const lonDeg = Math.atan2(y, x) * (180 / Math.PI);
  const direction: Vec3 = [x, y, z];
  const reps = Math.max(1, switches.reliefRepeat);
  let heightM = 0;
  // The class-map lookup is re-run each rep too, so reliefRepeat slows the whole per-sample cost, not just the noise call.
  for (let k = 0; k < reps; k++) {
    const { classes, duneOrientationDeg } = sampleClasses(maps.terrainMap, maps.duneMap, lonDeg, latDeg);
    const sample: ReliefSample = {
      direction,
      classes: switches.reliefUnconditioned
        ? { plains: 0, dune: 1, rugged: 0, lake: 0 }
        : classes,
      duneOrientationDeg,
    };
    heightM = reliefHeightM(sample, maps.config, cellM);
  }
  return heightM;
}

/** A tile's relief lattice: `(quads + 3)^2` unit body-fixed directions, row-major, one vertex wider than the grid each way. */
export interface TileReliefLattice {
  readonly n: number;
  readonly dirs: Float64Array;
  /** One mesh cell's ground length, which the relief fades its octaves against. */
  readonly cellM: number;
}

export function tileReliefLattice(bounds: TileBounds, radiusM: number, quads: number): TileReliefLattice {
  const n = quads + 3;
  const cellM = (Math.PI * radiusM) / (2 * 2 ** bounds.level * quads);
  const latU = new Float64Array(n);
  const latV = new Float64Array(n);
  for (let k = 0; k < n; k++) {
    const f = (k - 1) / quads;
    latU[k] = faceTangent(bounds.u0 + (bounds.u1 - bounds.u0) * f);
    latV[k] = faceTangent(bounds.v0 + (bounds.v1 - bounds.v0) * f);
  }
  const face = bounds.face;
  const axis = face % 2 === 0 ? 1 : -1;
  const dirs = new Float64Array(n * n * 3);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const a = latU[i]!;
      const c = latV[j]!;
      let x: number;
      let y: number;
      let z: number;
      if (face < 2) { x = axis; y = a; z = c; } else if (face < 4) { x = a; y = axis; z = c; } else { x = a; y = c; z = axis; }
      const inv = 1 / Math.hypot(x, y, z);
      const k3 = (j * n + i) * 3;
      dirs[k3] = x * inv;
      dirs[k3 + 1] = y * inv;
      dirs[k3 + 2] = z * inv;
    }
  }
  return { n, dirs, cellM };
}

/** Every lattice direction's height, metres before exaggeration. */
export function tileReliefHeights(
  maps: TileReliefMaps, switches: TileReliefSwitches, lattice: TileReliefLattice,
): Float64Array<ArrayBuffer> {
  const { n, dirs, cellM } = lattice;
  const out = new Float64Array(n * n);
  for (let k = 0; k < n * n; k++) {
    out[k] = reliefHeightAt(maps, switches, dirs[k * 3]!, dirs[k * 3 + 1]!, dirs[k * 3 + 2]!, cellM);
  }
  return out;
}

/** Builds a runtime sampler over two decoded class maps for one body. */
export function createTileReliefSampler(
  terrainMap: DecodedClassMap, duneMap: DecodedClassMap, config: ReliefConfig,
): TileReliefSampler {
  const maps: TileReliefMaps = { terrainMap, duneMap, config };
  const handle: TileReliefSampler = {
    heightM: (x, y, z, cellM) => reliefHeightAt(maps, handle, x, y, z, cellM),
    maps,
    reliefInert: false,
    reliefUnconditioned: false,
    reliefRepeat: 1,
  };
  return handle;
}
